import { alpha, Box, Typography } from '@mui/material';

import { SquareIcon } from '@/components';
import { useT } from '@/hooks';
import { colors } from '@/styles/theme';
import type { EventMarket, EventOutcome, EventResponse, Market, Outcome } from '@/types';
import { getEventType, getSpreadLine, isDarkColor } from '@/utils';

const MONO_FONT = 'var(--font-plex-mono), IBM Plex Mono, monospace';

interface TradeHeaderProps {
  event?: EventResponse;
  market?: Market | EventMarket;
  title: string;
  outcome?: Outcome | EventOutcome | null;
}

export function TradeHeader({ event, market, title, outcome }: TradeHeaderProps) {
  const { t } = useT();
  const eventType = getEventType(event);
  const isBinary = eventType === 'SIMPLE_BINARY' || eventType === 'CUSTOM_BINARY' || event?.gameId;

  const isSport = !!(event?.gameId || (event?.homeTeam && event?.awayTeam));
  const outcomeName = outcome?.name || '';

  const getSportDetails = () => {
    if (!isSport || !event) return null;

    const lowerName = outcomeName.toLowerCase().trim();
    const homeName = event.homeTeam?.name?.toLowerCase().trim() || '';
    const awayName = event.awayTeam?.name?.toLowerCase().trim() || '';
    const homeAbbr = event.homeTeam?.abbreviation?.toLowerCase().trim() || '';
    const awayAbbr = event.awayTeam?.abbreviation?.toLowerCase().trim() || '';

    const marketTitle =
      (market as any)?.groupItemTitle || (market as any)?.question || (market as any)?.title || '';
    const lowerMarketTitle = marketTitle.toLowerCase();
    const searchableText = `${lowerName} ${lowerMarketTitle}`;

    const detectTeamFromText = (text: string) => {
      const normalized = text.toLowerCase().trim();
      if (
        (homeName && (normalized.includes(homeName) || homeName.includes(normalized))) ||
        (homeAbbr && (normalized.includes(homeAbbr) || homeAbbr.includes(normalized)))
      ) {
        return event.homeTeam;
      }
      if (
        (awayName && (normalized.includes(awayName) || awayName.includes(normalized))) ||
        (awayAbbr && (normalized.includes(awayAbbr) || awayAbbr.includes(normalized)))
      ) {
        return event.awayTeam;
      }
      return null;
    };

    let matchingTeam: any = null;

    const isDraw =
      searchableText.includes('draw') ||
      lowerMarketTitle.includes('draw') ||
      lowerName.includes('draw') ||
      searchableText.includes('dibujar') ||
      lowerMarketTitle.includes('dibujar') ||
      lowerName.includes('dibujar');

    if (isDraw) {
      matchingTeam = {
        name: t('sports.draw', { defaultValue: 'Draw' }),
        abbreviation: t('sports.draw', { defaultValue: 'Draw' }).toUpperCase(),
        color: colors.neutral[500], // Slate grey for Draw
      } as any;
    } else {
      matchingTeam = detectTeamFromText(searchableText);
    }

    const type = (market as any)?.sportsMarketType || (market as any)?.sportsType || '';
    const isML = type.toLowerCase().includes('moneyline') || type.toLowerCase().includes('winner');
    const isTotal =
      type.toLowerCase().includes('total') ||
      type.toLowerCase().includes('over/under') ||
      lowerMarketTitle.includes('o/u');
    const line = (market as any)?.groupItemThreshold || (market as any)?.line || '';

    let finalOutcomeLabel = outcomeName;
    const lowerOutcome = outcomeName.toLowerCase();

    // If outcome is a generic Yes/No, prefer the more descriptive market title or selection name
    if (lowerOutcome === 'yes' || lowerOutcome === 'no') {
      const selectionLabel =
        (market as any)?.groupItemTitle ||
        (market as any)?.question ||
        (market as any)?.title ||
        '';
      if (selectionLabel) {
        finalOutcomeLabel = selectionLabel;
      }
    }

    // Resolve team using the final label first (e.g. "Flyers"),
    // then apply YES/NO inversion when market title represents one team.
    if (!isDraw) {
      const labelTeam = detectTeamFromText(finalOutcomeLabel);
      if (labelTeam) {
        matchingTeam = labelTeam;
      } else if (lowerOutcome === 'yes' || lowerOutcome === 'no') {
        const titleTeam = detectTeamFromText(lowerMarketTitle);
        if (titleTeam) {
          if (lowerOutcome === 'yes') {
            matchingTeam = titleTeam;
          } else {
            matchingTeam = titleTeam === event.homeTeam ? event.awayTeam : event.homeTeam;
          }
        }
      }

      // Contextual Label Mapping for Totals/Props in Header
      if (isTotal) {
        if (lowerOutcome === 'yes' || lowerOutcome === 'over') {
          finalOutcomeLabel = `OVER`.toUpperCase();
        } else if (lowerOutcome === 'no' || lowerOutcome === 'under') {
          finalOutcomeLabel = `UNDER`.toUpperCase();
        }
      }
    }

    const isSpread =
      type.toLowerCase().includes('spread') ||
      type.toLowerCase().includes('handicap') ||
      type === 'puck line';

    if (!isML && line && !finalOutcomeLabel.includes(String(line))) {
      if (isSpread) {
        const isAway = matchingTeam === event.awayTeam;
        const spreadLine = getSpreadLine(
          market as any,
          isAway ? event?.awayTeam?.name : event?.homeTeam?.name,
          isAway,
        );
        if (spreadLine) {
          finalOutcomeLabel = `${finalOutcomeLabel} ${spreadLine}`;
        } else {
          finalOutcomeLabel = `${finalOutcomeLabel}`;
        }
      } else {
        finalOutcomeLabel = `${finalOutcomeLabel}`;
      }
    }

    let displayColor = matchingTeam?.color || colors.info.main;
    if (isTotal) {
      displayColor =
        lowerOutcome.includes('under') || lowerOutcome === 'no' ? '#cb3131' : '#359a5e';
    }

    const hTitle = event?.homeTeam?.name;
    const aTitle = event?.awayTeam?.name;

    let displayTitle = title;
    const genericTitlePattern = /^home\s*vs\.?\s*away$/i;
    if (
      genericTitlePattern.test(displayTitle) &&
      event?.title &&
      !genericTitlePattern.test(event.title)
    ) {
      displayTitle = event.title;
    }

    const gameTitle = hTitle && aTitle ? `${hTitle} vs. ${aTitle}` : displayTitle;

    return {
      abbreviation: isDraw
        ? t('sports.draw', { defaultValue: 'Draw' }).toUpperCase()
        : matchingTeam?.abbreviation || homeAbbr.toUpperCase() || 'SPT',
      color: displayColor,
      logo: matchingTeam?.logo || event?.icon || event?.image || (market as any)?.image || '',
      gameTitle: isTotal
        ? 'Over vs Under'
        : isDraw
          ? `${t('sports.draw', { defaultValue: 'Draw' })}: ${gameTitle}`
          : gameTitle,
      outcomeLabel: finalOutcomeLabel,
      isTotal,
    };
  };

  const sportDetails = getSportDetails();

  if (isSport && sportDetails) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
          mb: 2.25,
          p: 0,
        }}
      >
        {sportDetails.isTotal ? (
          <Box
            sx={{
              width: 38,
              height: 38,
              bgcolor: 'transparent',
              background: 'linear-gradient(135deg, #359a5e 50%, #cb3131 50%)',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Typography
              sx={{
                position: 'absolute',
                top: 5,
                left: 7,
                color: '#fff',
                fontSize: '0.7rem',
                fontWeight: 900,
                lineHeight: 1,
              }}
            >
              O
            </Typography>
            <Typography
              sx={{
                position: 'absolute',
                bottom: 5,
                right: 7,
                color: '#fff',
                fontSize: '0.7rem',
                fontWeight: 900,
                lineHeight: 1,
              }}
            >
              U
            </Typography>
          </Box>
        ) : (
          <SquareIcon
            src={sportDetails.logo}
            alt={sportDetails.abbreviation || 'Team'}
            size={38}
            borderRadius="10px"
            style={{
              objectFit: 'contain',
              padding: '4px',
            }}
            fallback={
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: alpha(sportDetails.color, 0.18),
                }}
              >
                <Typography
                  sx={{
                    fontFamily: MONO_FONT,
                    fontSize: '0.6875rem',
                    fontWeight: 600,
                    color: isDarkColor(sportDetails.color) ? '#ffffff' : sportDetails.color,
                  }}
                >
                  {sportDetails.abbreviation}
                </Typography>
              </Box>
            }
          />
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.875, minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              fontFamily: 'var(--font-archivo), Archivo, sans-serif',
              fontWeight: 600,
              fontSize: '0.9375rem',
              lineHeight: 1.3,
              color: 'text.primary',
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {sportDetails.gameTitle}
          </Typography>
          <Box
            sx={{
              bgcolor: alpha(sportDetails.color, 0.15),
              color: sportDetails.color,
              px: 1,
              py: 0.375,
              borderRadius: '6px',
              border: '1px solid',
              borderColor: alpha(sportDetails.color, 0.3),
              width: 'fit-content',
              maxWidth: '100%',
              fontFamily: MONO_FONT,
              fontSize: '0.65625rem',
              fontWeight: 600,
              letterSpacing: '0.02em',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {sportDetails.outcomeLabel}
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        mb: 3,
        p: 1,
      }}
    >
      {!isBinary && (
        <Box
          component="img"
          src={market?.image || event?.image}
          sx={{
            width: 48,
            height: 48,
            borderRadius: 1.5,
            objectFit: 'cover',
          }}
          alt={title === 'Home vs. Away' && event?.title ? event.title : title || 'Market'}
        />
      )}
      <Typography
        variant="h6"
        sx={{
          fontWeight: 700,
          fontSize: '1.125rem',
          lineHeight: 1.3,
          color: 'text.primary',
        }}
      >
        {(() => {
          const genericTitlePattern = /^home\s*vs\.?\s*away$/i;
          if (
            genericTitlePattern.test(title) &&
            event?.title &&
            !genericTitlePattern.test(event.title)
          ) {
            return event.title;
          }
          return title;
        })()}
      </Typography>
    </Box>
  );
}
